// ── 매입 관리 데이터 훅 ──────────────────────────────────────────────────────
// 회사별 (PINE/SEMECOLON) 매입 행 + 배치 목록 + 줄 고치기·지우기 + 엑셀 업로드.
// 2026-10-08: update 는 { ok, message } 를 돌려준다(수정 창이 오류를 창 안에 보여 줌). removeMany = 고른 줄 한 번에 지우기.

import { useCallback, useEffect, useState } from 'react';
import { apiClient } from '../../../api/apiClient';

export function useCashflowPurchase({ companyCode, showToast }) {
  const [rows, setRows] = useState([]);
  const [batches, setBatches] = useState([]);
  const [loading, setLoading] = useState(false);
  const [tick, setTick] = useState(0);
  const reload = useCallback(() => setTick((t) => t + 1), []);

  useEffect(() => {
    let alive = true;
    setLoading(true);
    (async () => {
      const [r1, r2] = await Promise.all([
        apiClient.get(`/api/admin/cashflow/purchases?company_code=${companyCode}`),
        apiClient.get(`/api/admin/cashflow/purchases/batches?company_code=${companyCode}`),
      ]);
      if (!alive) return;
      if (r1.ok && r1.data?.success) setRows(r1.data.data || []);
      else { showToast?.(r1.data?.message || '매입 목록 조회 실패', 'error'); setRows([]); }
      if (r2.ok && r2.data?.success) {
        setBatches(r2.data.data || []);
      } else {
        showToast?.(r2.data?.message || `업로드 이력 조회 실패 (status ${r2.status || '?'})`, 'error');
        setBatches([]);
      }
      setLoading(false);
    })();
    return () => { alive = false; };
  }, [companyCode, tick, showToast]);

  // 직접 입력 한 줄(2026-11-03) — { ok, duplicate, message }. 같은 거래가 이미 있으면 duplicate=true
  //   (창이 한 번 묻고 allowDuplicate 로 다시 보낸다). 오류는 창 안에 보여 주므로 여기서 띄우지 않는다.
  const create = useCallback(async (body, { allowDuplicate = false } = {}) => {
    const res = await apiClient.post('/api/admin/cashflow/purchases', {
      company_code: companyCode, ...body, ...(allowDuplicate ? { allow_duplicate: true } : {}),
    });
    if (!res.ok || !res.data?.success) {
      return { ok: false, duplicate: Boolean(res.data?.duplicate), message: res.data?.message || `추가 실패 (status ${res.status || '?'})` };
    }
    showToast?.('한 줄 추가했습니다.', 'success');
    reload();
    return { ok: true, row: res.data.data };
  }, [companyCode, reload, showToast]);

  const update = useCallback(async (id, body) => {
    const res = await apiClient.put(`/api/admin/cashflow/purchases/${id}`, body);
    if (!res.ok || !res.data?.success) {
      return { ok: false, message: res.data?.message || `저장 실패 (status ${res.status || '?'})` };
    }
    showToast?.('수정했습니다.', 'success');
    reload();
    return { ok: true, row: res.data.data };
  }, [reload, showToast]);

  const removeMany = useCallback(async (ids) => {
    const res = await apiClient.post(`/api/admin/cashflow/purchases/bulk-delete?company_code=${companyCode}`, {
      company_code: companyCode,
      ids,
    });
    if (!res.ok || !res.data?.success) {
      showToast?.(res.data?.message || '삭제 실패', 'error');
      reload();
      return false;
    }
    showToast?.(`${res.data.data?.deleted || 0}건 삭제했습니다.`, 'success');
    reload();
    return true;
  }, [companyCode, reload, showToast]);

  const upload = useCallback(async (file) => {
    const fd = new FormData();
    fd.append('file', file);
    fd.append('company_code', companyCode);
    const res = await apiClient.postForm
      ? await apiClient.postForm(`/api/admin/cashflow/purchases/upload?company_code=${companyCode}`, fd)
      : await fetch(`/api/admin/cashflow/purchases/upload?company_code=${companyCode}`, {
          method: 'POST',
          headers: { Authorization: `Bearer ${localStorage.getItem('accessToken') || ''}` },
          body: fd,
        }).then(async (r) => ({ ok: r.ok, data: await r.json().catch(() => ({})) }));
    if (!res.ok || !res.data?.success) {
      showToast?.(res.data?.message || '업로드 실패', 'error');
      return null;
    }
    showToast?.(`업로드 완료 — 신규 ${res.data.data.inserted || 0}건 · 갱신 ${res.data.data.updated || 0}건 · 중복 skip ${res.data.data.skipped || 0}건`, 'success');
    reload();
    return res.data.data;
  }, [companyCode, reload, showToast]);

  const pasteRows = useCallback(async (payloadRows) => {
    const res = await apiClient.post(`/api/admin/cashflow/purchases/paste?company_code=${companyCode}`, {
      company_code: companyCode,
      rows: payloadRows,
    });
    if (!res.ok || !res.data?.success) {
      showToast?.(res.data?.message || '붙여넣기 저장 실패', 'error');
      return false;
    }
    const d = res.data.data || {};
    showToast?.(`붙여넣기 완료 — 신규 ${d.inserted || 0}건 · 갱신 ${d.updated || 0}건`, 'success');
    reload();
    return true;
  }, [companyCode, reload, showToast]);

  const rollback = useCallback(async (batchId) => {
    if (!window.confirm('이 묶음을 삭제할까요?\n이 묶음으로 새로 들어온 줄은 수정한 줄까지 함께 삭제됩니다.\n(갱신으로 덮어쓴 기존 줄의 값은 되돌아가지 않습니다.)')) return false;
    const res = await apiClient.delete(`/api/admin/cashflow/purchases/batch/${batchId}?company_code=${companyCode}`);
    if (!res.ok || !res.data?.success) {
      showToast?.(res.data?.message || '삭제 실패', 'error');
      return false;
    }
    showToast?.(`배치 삭제 — 행 ${res.data.data?.deleted || 0}건 제거됨`, 'success');
    reload();
    return true;
  }, [companyCode, reload, showToast]);

  // 고른 줄 색 표시(2026-11-03) — color: 'yellow'|'green'|'blue'|'red'|null(지우기)
  const setColor = useCallback(async (ids, color) => {
    const res = await apiClient.post(`/api/admin/cashflow/purchases/colors?company_code=${companyCode}`, {
      company_code: companyCode, ids, color,
    });
    if (!res.ok || !res.data?.success) {
      showToast?.(res.data?.message || '색을 바꾸지 못했습니다.', 'error');
      return false;
    }
    reload();
    return true;
  }, [companyCode, reload, showToast]);

  return { rows, batches, loading, reload, create, update, removeMany, upload, pasteRows, rollback, setColor };
}
